import datapoints from '../datapoints';
import { logUsage } from 'owa-analytics';
const namespace = 'EDITOR_PERF';

const { datapoint: dropedFrameDatapoint } = datapoints.DropppedFrameWhileTyping;

/**
 * Number of frames to measure after the last keydown event.
 *
 * If a new keydown event happens in this interval, the capturing window will
 * be expanded to MINIMUM_FRAME_WINDOW after that keydown event, and so on for
 * subsequent keydown events
 */
const MINIMUM_FRAME_WINDOW = 5;

// Average time per-frame time considered to be a dropped frame
// This corresponds to <=40fps over the time spend typing
const AVERAGE_FRAME_TIME_THRESHOLD_MS = 25;

export function register(target: HTMLElement, name: string): () => void {
    const startName = `${namespace}::typingIntervalStart::${name}`;
    const endName = `${namespace}::typingIntervalEnd::${name}`;
    const measureName = `${namespace}::typingInterval::${name}`;

    // Defend against performance not being implemented fully on older browsers (e.g. old safari which we support)
    if (
        !performance ||
        !performance.mark ||
        !performance.measure ||
        !performance.now ||
        !performance.clearMarks
    ) {
        return () => {};
    }

    let activeRaf = null;
    let remainingFrames = 0;

    let numFramesCounted = 0;
    let startTime = null;

    const measureOnKeyHandler = () => {
        // Always bump the remaining frames, even if we have an active raf
        remainingFrames = MINIMUM_FRAME_WINDOW;
        if (activeRaf !== null) {
            // We already have a raf cycle established, do nothing else in the handler
            return;
        }

        // Start capturing
        performance.mark(startName);
        startTime = performance.now();
        numFramesCounted = 0;

        activeRaf = requestAnimationFrame(function onRaf() {
            numFramesCounted++;
            if (--remainingFrames > 0) {
                activeRaf = requestAnimationFrame(onRaf);
                return;
            }

            // We are at the end of the frame interval.
            activeRaf = null;
            const endTime = performance.now();

            // Record period of typing in the performance trace
            performance.mark(endName);
            performance.measure(measureName, startName, endName);
            performance.clearMarks(startName);
            performance.clearMarks(endName);

            const averageFrameTime = (endTime - startTime) / numFramesCounted;
            if (averageFrameTime > AVERAGE_FRAME_TIME_THRESHOLD_MS) {
                logUsage(
                    dropedFrameDatapoint.name,
                    dropedFrameDatapoint.customData({
                        time: averageFrameTime,
                    })
                );
            }
        });
    };

    // Use capturing phase to trigger before other handles
    target.addEventListener('keydown', measureOnKeyHandler, true /* useCapture */);
    return () => {
        target.removeEventListener('keydown', measureOnKeyHandler, true /* useCapture */);
    };
}
